import moment from 'moment'
import { FRACTIONS, FRACTIONS_TYPES } from '@/utils/constants/drs/fractions'
import { DEVICE_NAMES } from '@/utils/constants/drs/store-devices.js'
import { STATES as BAG_STATES, BAG_STATE_NAMES, MODES } from '@/utils/constants/drs/bags'

export const SEAL_NUMBER_VALID_LENGTH = 15

const OWN_TRANSPORT = {
    id: 'OWN_TRANSPORT',
    name: 'Transport własny'
}

const MODE_RULES = {
    [MODES.MERGE]: {
        isSelectable: (pkg, ctx) => {
            // only open bags
            if (pkg.isClosed) return false

            // exclude RVM bags
            if (pkg.device_type === DEVICE_NAMES.RVM) return false

            // allow if configurationFractionMode is MIX
            if (ctx.configurationFractionMode === 'MIX') return true

            const ref = ctx.selectedPackages[0]
            //  allow if no bag selected yet
            if (!ref) return true

            // allow merge same fraction
            return pkg.fractionTypeId === ref.fractionTypeId
        },

        allowSelectAll: (filteredPackages) => {
            return filteredPackages.every(pkg => pkg.fractionTypeId === filteredPackages[0]?.fractionTypeId)
        }
    },
    [MODES.TRANSFER]: {
        isSelectable: (pkg, ctx) => {
            // only closed bags
            if (!pkg.isClosed) return false

            const ref = ctx.selectedPackages[0]

            //  allow if no bag selected yet
            if (!ref) return true

            // allow transfer same operator
            return pkg.selectedOperator?.id === ref.selectedOperator?.id
        },

        allowSelectAll: (filteredPackages) => {
            return filteredPackages.every(pkg => pkg.selectedOperator?.id === filteredPackages[0]?.selectedOperator?.id)
        }
    }
}

const isMixFraction = fractions => {
    if (!fractions || fractions.length !== 2) return false

    const requiredIds = [
        FRACTIONS[FRACTIONS_TYPES.PLASTIC].id,
        FRACTIONS[FRACTIONS_TYPES.METAL].id
    ]

    const fractionIds = fractions.map(f => f.id)

    return requiredIds.every(id => fractionIds.includes(id))
}

function buildDeviceHeaders (deviceCredentials) {
    if (!deviceCredentials) return {}

    return {
        DeviceToken: `Bearer ${deviceCredentials.token}`,
        CryptoKey: deviceCredentials.crypto_key
    }
}

const state = () => ({
    packages: [],
    configuration: {},
    services: [],
    deviceCredentials: {},
    mode: MODES.IDLE,
    selectedIds: []
})

const mutations = {
    SET_PACKAGES(state, payload) {
        state.packages = payload
    },

    SET_CONFIGURATION(state, configuration) {
        state.configuration = {
            ...configuration,
            fractions: (configuration.fractions || []).reduce((acc, item) => {
                acc[item.id] = item
                return acc
            }, {})
        }
    },

    SET_SERVICES(state, payload) {
        state.services = payload
    },

    SET_DEVICE_CREDENTIALS(state, payload) {
        state.deviceCredentials = payload
    },

    SET_MODE(state, mode) {
        state.mode = mode
        state.selectedIds = []
    },

    SET_SELECTED_IDS(state, ids) {
        state.selectedIds = ids
    },

    TOGGLE_PACKAGE_IN_MODE(state, packageId) {
        const arr = state.selectedIds

        state.selectedIds = arr.includes(packageId)
            ? arr.filter(id => id !== packageId)
            : [...arr, packageId]
    }
}

const actions = {
    async loadPackages({ commit, rootGetters }, payload = {}) {
        // get selected locationId from filters
        const locationId = rootGetters['drsStores/getFilterLocationId']
        if (!locationId) return

        // The API cannot return all packages, so request 500 results at a time
        const batchSize = 500
        const loadPage = async page => {
            const { data, status } = await this.$api.post(
                'drs/api/proxy/warehouse/drs/reports/packages',
                {
                    ...payload,
                    location_ids: [locationId],
                    paging: {
                        number_of_page: page,
                        number_of_rows: batchSize,
                        order_by: 'close_date',
                        order_direction: 'DESC'
                    },
                    include_eans: false
                },
                { config: { noCancel: true } }
            )

            if (status !== 200) {
                throw new Error('Failed fetchPackages')
            }

            return data
        }

        try {
            // Use the first response count to determine how many requests are required
            const firstPage = await loadPage(1)
            const totalPages = Math.ceil(
                (Number(firstPage?.paging_response?.count) || 0) / batchSize
            )
            // Send the remaining requests and merge all packages into one list
            const remainingPages = await Promise.all(
                Array.from(
                    { length: Math.max(totalPages - 1, 0) },
                    (_, index) => loadPage(index + 2)
                )
            )

            if (locationId !== rootGetters['drsStores/getFilterLocationId']) return

            commit(
                'SET_PACKAGES',
                [firstPage, ...remainingPages].flatMap(data => data?.packages || [])
            )
        } catch (error) {
            console.error(error)
        }
    },

    async loadDeviceCredentials({ commit, rootGetters }) {
        // get selected locationId from filters
        const locationId = rootGetters['drsStores/getFilterLocationId']
        if (!locationId) return

        try {
            const { data, status } = await this.$api.get(
                `drs/api/proxy/users/[drs_user_id]/locations/${locationId}/devices`
            )

            if (status !== 200 || !data) {
                throw new Error('Failed fetchDeviceCredentials')
            }

            commit('SET_DEVICE_CREDENTIALS', data)
        } catch (error) {
            console.error(error)
        }
    },

    async loadConfiguration({ commit, rootGetters }) {
        // get selected locationId from filters
        const locationId = rootGetters['drsStores/getFilterLocationId']
        if (!locationId) return

        try {
            const { data, status } = await this.$api.get(
                `drs/api/proxy/entity/location/configuration/${locationId}`,
                { config: { noCancel: true } }
            )

            if (status !== 200 || !data) {
                throw new Error('Failed fetchConfiguration')
            }

            commit('SET_CONFIGURATION', data)
        } catch (error) {
            console.error(error)
        }
    },

    async loadServices({ commit }) {
        try {
            const { data, status } = await this.$api.get(
                'drs/api/proxy/subscriptions/[drs_user_id]/services?operatorService=true',
                { config: { noCancel: true } }
            )

            if (status !== 200) {
                throw new Error('Failed loadServices')
            }

            const mappedServices = (data.services || []).map(service => ({
                ...service,
                sealPrefix: service.features.find(f => f.code === 'SEAL_PREFIX')?.description
            }))

            commit('SET_SERVICES', mappedServices)
        } catch (error) {
            console.error(error)
        }
    },

    async closePackage({ dispatch, rootGetters, state }, payload) {
        // get selected locationId from filters
        const locationId = rootGetters['drsStores/getFilterLocationId']
        if (!locationId) return

        try {
            const { status, data } = await this.$api.post(
                'drs/api/proxy/drs/web/package',
                {
                    cashier: rootGetters.getUser.email,
                    close_date: moment().unix(),
                    mcuid: payload.mcuid,
                    package_content: payload.fractions.map(({ id }) => ({ fraction: id.toString() })),
                    seal_number: payload.seal_number,
                    package_id: payload.package_id
                },
                {
                    headers: buildDeviceHeaders(state.deviceCredentials)
                }
            )

            if (status !== 200) {
                throw data
            }

            // update packages
            await dispatch('loadPackages')

            dispatch('addNotification',
                {
                    type: 'success',
                    content: 'Worek został zamknięty'
                },
                { root: true }
            )
        } catch (error) {
            console.error(error)
            dispatch('addNotification',
                {
                    type: 'danger',
                    content: error.response.data.message || 'Wystąpił błąd'
                },
                { root: true }
            )
        }
    },

    async changeSealNumber({ dispatch, rootGetters, state }, payload) {
        // get selected locationId from filters
        const locationId = rootGetters['drsStores/getFilterLocationId']
        if (!locationId) return

        try {
            const { status, data } = await this.$api.patch(
                'drs/api/proxy/drs/web/package/seal',
                {
                    cashier: rootGetters.getUser.email,
                    change_reason: payload.changeReason,
                    change_seal_date: moment().unix(),
                    mcuid: payload.mcuid,
                    new_seal_number: payload.newSealNumber,
                    old_seal_number: payload.oldSealNumber
                },
                {
                    headers: buildDeviceHeaders(state.deviceCredentials)
                }
            )

            if (status !== 200) {
                throw data
            }

            dispatch('addNotification',
                {
                    type: 'success',
                    content: 'Numer plomby został zmieniony'
                },
                { root: true }
            )
        } catch (error) {
            console.error(error)
            dispatch('addNotification',
                {
                    type: 'danger',
                    content: error.response.data.message || 'Wystąpił błąd'
                },
                { root: true }
            )
        }
    },

    async mergePackages({ dispatch, rootGetters, state }, payload) {
        // get selected locationId from filters
        const locationId = rootGetters['drsStores/getFilterLocationId']
        if (!locationId) return

        try {
            const { status } = await this.$api.patch(
                'drs/api/proxy/drs/web/package/merge',
                {
                    cashier: rootGetters.getUser.email,
                    mcuid: state.deviceCredentials.mcuid,
                    merge_packages_date: moment().unix(),
                    merge_packages: payload.mergePackages,
                    target_package: payload.targetPackages
                },
                {
                    headers: buildDeviceHeaders(state.deviceCredentials)
                }
            )

            if (status !== 200) {
                throw new Error('Failed mergeBags')
            }

            dispatch('addNotification',
                {
                    type: 'success',
                    content: 'Worki zostały połączone'
                },
                { root: true }
            )
        } catch (error) {
            console.error(error)
            dispatch('addNotification',
                {
                    type: 'danger',
                    content: 'Wystąpił błąd'
                },
                { root: true }
            )
        }
    },

    setMode({ commit }, mode) {
        commit('SET_MODE', mode)
    },

    resetMode({ commit }) {
        commit('SET_MODE', MODES.IDLE)
        commit('drsStores/SET_FILTER', {
            filter: 'bagState',
            value: ''
        }, { root: true }
        )
    },

    togglePackageInMode({ commit }, packageId) {
        commit('TOGGLE_PACKAGE_IN_MODE', packageId)
    },

    selectAllPackages({ getters, commit }, checkboxValue) {
        if (!checkboxValue) {
            commit('SET_SELECTED_IDS', [])
            return
        }

        commit('SET_SELECTED_IDS', getters.selectablePackageIds)
    }
}

const getters = {
    basePackages: state => state.packages || [],

    packages: state => state.packages.filter(pkg => pkg.fractions?.length).map(pkg => {
        const fractionId = pkg.fractions?.[0]?.id
        const configFraction = fractionId ? state.configuration.fractions[fractionId] : null
        const dictOperatorId = configFraction?.dict_operator_id
        const isClosed = typeof pkg.seal_number === 'string'
        const sealPrefix = isClosed ? pkg.seal_number.slice(0, 2) : null

        let preferredOperator = null
        let sealOperator = null
        let selectedOperator = null

        for (const service of state.services) {
            const features = service.features || []

            const operatorId = features.find(f => f.code === 'OPERATOR_ID')?.description

            // Preferred operator
            if (dictOperatorId && operatorId === String(dictOperatorId)) {
                preferredOperator = {
                    id: service.id,
                    name: service.name,
                    prefix: service.sealPrefix || null
                }
            }

            // Operator from seal number
            if (sealPrefix && service.sealPrefix === sealPrefix) {
                sealOperator = {
                    id: service.id,
                    name: service.name,
                    prefix: sealPrefix
                }
            }

            // Early exit
            if (preferredOperator && (!sealPrefix || sealOperator)) {
                break
            }
        }

        // set selected operator
        if (isClosed) {
            selectedOperator = sealOperator || {
                ...OWN_TRANSPORT,
                prefix: null
            }
        } else {
            selectedOperator = preferredOperator
        }

        // set fraction type id
        const fractionTypeId = isMixFraction(pkg.fractions)
            ? FRACTIONS[FRACTIONS_TYPES.MIX].id
            : fractionId || null

        return {
            ...pkg,
            preferredOperator,
            fractionTypeId,
            selectedOperator,
            isClosed
        }
    }),

    filteredPackages: (state, getters, _, rootGetters) => {
        const filters = rootGetters['drsStores/getFilters']

        if (!filters) return getters.packages

        let packages = getters.packages

        const selectedFractionId = filters.bagFraction.value
        if (selectedFractionId) {
            packages = packages.filter(pkg => pkg.fractionTypeId === selectedFractionId)
        }

        const selectedBagState = filters.bagState.value
        if (selectedBagState) {
            packages = packages.filter(pkg => {
                const wantsActive = selectedBagState === BAG_STATES.OPENED
                const wantsInactive = selectedBagState === BAG_STATES.CLOSED
                return (wantsActive && !pkg.isClosed) || (wantsInactive && pkg.isClosed)
            })
        }

        const selectedBagOperator = filters.bagOperator.value
        if (selectedBagOperator) {
            packages = packages.filter(pkg => pkg.selectedOperator?.id === selectedBagOperator)
        }

        return packages
    },

    bagStatesForSelect: () => [
        { label: BAG_STATE_NAMES[BAG_STATES.CLOSED], value: BAG_STATES.CLOSED },
        { label: BAG_STATE_NAMES[BAG_STATES.OPENED], value: BAG_STATES.OPENED }
    ],

    bagFractionsForSelect: () => [
        { label: FRACTIONS[FRACTIONS_TYPES.PLASTIC].name, value: FRACTIONS[FRACTIONS_TYPES.PLASTIC].id },
        { label: FRACTIONS[FRACTIONS_TYPES.METAL].name, value: FRACTIONS[FRACTIONS_TYPES.METAL].id },
        { label: FRACTIONS[FRACTIONS_TYPES.GLASS].name, value: FRACTIONS[FRACTIONS_TYPES.GLASS].id },
        { label: FRACTIONS[FRACTIONS_TYPES.MIX].name, value: FRACTIONS[FRACTIONS_TYPES.MIX].id }
    ],

    bagOperatorsForSelect: state => [
        ...state.services.map(({ name, id }) => ({ label: name, value: id })),
        { label: OWN_TRANSPORT.name, value: OWN_TRANSPORT.id }
    ],

    mode: state => state.mode,

    isPackageSelectable: (state, getters) => pkg => {
        const { mode, selectedPackages } = getters
        if (mode === MODES.IDLE) return false

        return MODE_RULES[mode].isSelectable(pkg, {
            selectedPackages,
            configurationFractionMode: state.configuration.package_fraction_mode
        })
    },

    isPackageSelected: state => packageId => state.selectedIds.includes(packageId),

    selectedPackageIds: state => state.selectedIds,

    selectedPackages: (state, getters) => {
        return getters.packages.filter(p =>
            state.selectedIds.includes(p.package_id)
        )
    },

    selectablePackageIds: (_, getters) => {
        return getters.filteredPackages
            .filter(pkg => getters.isPackageSelectable(pkg))
            .map(pkg => pkg.package_id)
    },

    deviceHeaders: state => buildDeviceHeaders(state.deviceCredentials),

    allowSelectAll: (_, getters) => {
        const mode = getters.mode
        if (mode === MODES.IDLE) return false

        return MODE_RULES[mode].allowSelectAll(getters.filteredPackages)
    },

    validSealPrefixes: state => {
        return state.services.map(service => service.sealPrefix)
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
